import * as Sentry from "@sentry/nextjs"
import { Button, Spinner } from "flowbite-react"
import { useRouter } from "next/router"
import React, { useCallback, useEffect, useRef, useState } from "react"

import useAuth from "@hooks/useAuth"
import useToast from "@hooks/useToast"
import routes from "@mono/essentials/constants/routes"
import * as CustomerServices from "@mono/essentials/services/CustomerService"
import { SendOtpFieldType, SendOtpResponseType } from "@mono/essentials/types/CustomerTypes"
import { getDomainName } from "@mono/essentials/utils/Common"

export interface CustomerOtpVerificationFormProps {
  email?: string
  isRedirectEnabled?: boolean
  onSuccess?: () => Promise<void> | void
  isIntegratedPage?: boolean
  onSuccessCustomerLogin?: () => void
  initialSendOtpResponse: SendOtpResponseType
}

const OTP_LENGTH = 6
let resendOtpTimer
let resendOtpDurationInSeconds: number
let otpExpirationTimer
let otpExpirationDurationInSeconds: number
const CustomerOtpVerificationForm = ({
  email,
  isRedirectEnabled,
  onSuccess,
  isIntegratedPage,
  onSuccessCustomerLogin,
  initialSendOtpResponse,
}: CustomerOtpVerificationFormProps) => {
  const router = useRouter()
  const { showToast } = useToast()
  const {
    setToken,
    setIsCustomerLoggedIn,
    setLoggedInCustomerDetails,
    publicDomainDetails,
    setSelectedTenant,
  } = useAuth() ?? {}

  const [isRequestingOtp, setIsRequestingOtp] = useState<boolean>(false)
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const [sendOtpResponse, setSendOtpResponse] =
    useState<SendOtpResponseType>(initialSendOtpResponse)
  const [showResend, setShowResend] = useState<boolean>(false)
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""))
  const inputs = useRef([])
  const [formError, setFormError] = useState<string>()

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { value } = e.target
    if (value.length === 1) {
      const oldOtp = [...otp]
      oldOtp[index] = value
      setOtp(oldOtp)

      // Move focus to the next input
      if (index < OTP_LENGTH - 1) {
        inputs.current[index + 1].focus()
      }
    } else if (value.length === 2) {
      const oldOtp = [...otp]
      oldOtp[index] = value.slice(-1)
      setOtp(oldOtp)

      // Move focus to the next input
      if (index < OTP_LENGTH - 1) {
        inputs.current[index + 1].focus()
      }
    } else if (value.length === OTP_LENGTH) {
      setOtp(value.split(""))

      // Move focus to the last input
      inputs.current[OTP_LENGTH - 1].focus()
    } else if (value.length < OTP_LENGTH) {
      setOtp(() =>
        Array(OTP_LENGTH)
          .fill("")
          .map((defaultValue, defaultValueIndex) => {
            if (value[defaultValueIndex]) return value[defaultValueIndex]
            return defaultValue
          })
      )

      // Move focus next to the input with valid value
      inputs.current[value.length === OTP_LENGTH ? OTP_LENGTH - 1 : value.length].focus()
    } else if (value.length > OTP_LENGTH) {
      setOtp(value.slice(0, OTP_LENGTH).split(""))

      // Move focus to the last input
      inputs.current[OTP_LENGTH - 1].focus()
    }

    // Move focus to previous input on backspace
    if (value === "" && index > 0) {
      inputs.current[index - 1].focus()
    }
  }

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault() // Prevent the default backspace action

      const newOtp = [...otp]

      // Clear the current input value
      newOtp[index] = ""
      setOtp(newOtp)

      // Move focus to the previous input if it exists
      if (index > 0) {
        inputs.current[index - 1].focus()
      }
    } else if (e.key === "Enter") {
      // Move focus to the next input
      if (index < OTP_LENGTH - 1) {
        inputs.current[index + 1].focus()
      }
    }
  }

  const handleVerifyOtp = async () => {
    setFormError("")
    setIsVerifyingOtp(true)
    try {
      const { data: loginResponse } = await CustomerServices.verifyOtp(
        {
          field_type: SendOtpFieldType.EMAIL,
          value: email,
          type: "Customer",
          otp_code: otp.join(""),
        },
        isIntegratedPage
      )
      if (onSuccess) {
        await onSuccess()
      } else {
        if (loginResponse?.token.access_token) {
          setToken?.(loginResponse.token.access_token)
          setIsCustomerLoggedIn?.(true)
          setLoggedInCustomerDetails?.(loginResponse.customer)
          setSelectedTenant?.(loginResponse.tenant.id)
          if (isRedirectEnabled) {
            router.push(
              `${window.location.protocol}//${publicDomainDetails.domain}.${getDomainName(true)}${routes.customer.orders.root}`
            )
          }
          onSuccessCustomerLogin?.()
        } else {
          setFormError("Something went wrong! please try again.")
        }
      }
    } catch (e) {
      Sentry.captureException(e)
      showToast({
        heading:
          e?.response?.data?.message ??
          e?.message ??
          "Unable to send the one-time password! please try again later.",
        type: "danger",
      })
      setOtp(Array(OTP_LENGTH).fill(""))
    } finally {
      setIsVerifyingOtp(false)
    }
  }

  const handleSendOtp = useCallback(async () => {
    setIsRequestingOtp(true)
    try {
      const { data: sendOtpResponse } = await CustomerServices.sendOtp(
        {
          field_type: SendOtpFieldType.EMAIL,
          value: email,
          type: "Customer",
        },
        isIntegratedPage
      )
      if (sendOtpResponse) {
        setSendOtpResponse(sendOtpResponse)
        showToast({
          heading: sendOtpResponse.message,
        })
        resendOtpDurationInSeconds = sendOtpResponse.cooldown_remaining_seconds
        otpExpirationDurationInSeconds = sendOtpResponse.expiry
        startResendOtpTimer()
        stopOtpExirationTimer(sendOtpResponse)
        startOtpExirationTimer()
      }
    } catch (e) {
      Sentry.captureException(e)
      showToast({
        heading:
          e?.response?.data?.message ??
          e?.message ??
          "Unable to send the one-time password! please try again later.",
        type: "danger",
      })
      setSendOtpResponse((prev) => ({
        ...prev,
        ...(e?.response?.data ?? {}),
      }))
      resendOtpDurationInSeconds = e?.response?.data?.cooldown_remaining_seconds
      otpExpirationDurationInSeconds = e?.response?.data?.expiry
    } finally {
      setIsRequestingOtp(false)
    }
  }, [sendOtpResponse, resendOtpDurationInSeconds])

  useEffect(() => {
    if (initialSendOtpResponse) {
      setSendOtpResponse(initialSendOtpResponse)
      resendOtpDurationInSeconds = initialSendOtpResponse.cooldown_remaining_seconds
      otpExpirationDurationInSeconds = initialSendOtpResponse.expiry
      startResendOtpTimer()
      stopOtpExirationTimer(initialSendOtpResponse)
      startOtpExirationTimer()
    }
  }, [initialSendOtpResponse])

  useEffect(() => {
    if (sendOtpResponse?.attempts_left === 0) {
      setFormError(
        `You exhausted the resend attempts! please try again in ${formatSecounds(sendOtpResponse.reattempt_remaining_seconds)} ${sendOtpResponse.reattempt_remaining_seconds > 120 ? "minutes" : "minute"}.`
      )
    } else {
      setFormError("")
    }
  }, [sendOtpResponse])

  const startResendOtpTimer = useCallback(() => {
    setShowResend(false)

    // Start the countdown
    resendOtpTimer = setInterval(updateResendOtpTimer, 1000)
  }, [sendOtpResponse, resendOtpDurationInSeconds, showResend])

  const stopOtpExirationTimer = useCallback(
    (resp?: SendOtpResponseType) => {
      otpExpirationDurationInSeconds = resp?.expiry || sendOtpResponse?.expiry
      clearInterval(otpExpirationTimer)
    },
    [sendOtpResponse, otpExpirationDurationInSeconds]
  )
  const startOtpExirationTimer = useCallback(() => {
    // Start the countdown
    otpExpirationTimer = setInterval(updateOtpExpirationTimer, 1000)
  }, [sendOtpResponse, otpExpirationDurationInSeconds])

  const updateResendOtpTimer = useCallback(() => {
    const timerElement = document.getElementById("otp-resend-timer")

    if (resendOtpDurationInSeconds > 0) {
      if (timerElement)
        timerElement.textContent = `Resend in ${formatSecounds(resendOtpDurationInSeconds ?? 0)} seconds`
      resendOtpDurationInSeconds--
    } else {
      setShowResend(true)

      if (timerElement) timerElement.textContent = ""
      // Reset countdown for the next attempt
      resendOtpDurationInSeconds = sendOtpResponse?.cooldown_remaining_seconds
      // Stop the timer
      clearInterval(resendOtpTimer)
    }
  }, [sendOtpResponse, resendOtpDurationInSeconds, showResend])

  const updateOtpExpirationTimer = useCallback(() => {
    const timerElement = document.getElementById("otp-expiration-timer")

    if (otpExpirationDurationInSeconds > 0) {
      if (timerElement) {
        timerElement.className = "text-neutral-900"
        timerElement.textContent = `${formatSecounds(otpExpirationDurationInSeconds)} ${otpExpirationDurationInSeconds > 120 ? "minutes" : "minute"}.`
      }
      otpExpirationDurationInSeconds--
    } else {
      if (timerElement) {
        timerElement.className = "text-red-600"
        timerElement.textContent = "00:00 seconds"
      }

      // Reset countdown for the next attempt
      otpExpirationDurationInSeconds = sendOtpResponse?.expiry
      // Stop the timer
      clearInterval(otpExpirationTimer)
    }
  }, [sendOtpResponse, otpExpirationDurationInSeconds])

  const formatSecounds = (duration: number) => {
    // Hours, minutes and seconds
    const hrs = ~~(duration / 3600)
    const mins = ~~((duration % 3600) / 60)
    const secs = ~~duration % 60

    // Output like "1:01" or "4:03:59" or "123:03:59"
    let ret = ""

    if (hrs > 0) {
      ret += `${hrs}:${mins < 10 ? "0" : ""}`
    }

    ret += `${mins}:${secs < 10 ? "0" : ""}`
    ret += `${secs}`

    return ret
  }

  return (
    <div className={"flex flex-col items-start gap-5"}>
      <div className={"flex flex-col items-start text14Regular text-neutral-900 gap-1.5"}>
        <p>
          Sent one-time password to <span className={"text14Semibold"}>{email}</span>
        </p>
        {sendOtpResponse?.expiry ? (
          <p>
            The one-time password is valid for the next <span id={"otp-expiration-timer"} />
          </p>
        ) : null}
      </div>
      <div className={"flex flex-col items-start gap-4 w-full"}>
        <div className={"flex flex-row items-center justify-between gap-4 w-full"}>
          <div className={"flex flex-row items-center gap-2.5"}>
            {otp.map((_, index) => (
              <input
                key={index}
                type={"text"}
                maxLength={OTP_LENGTH}
                value={otp[index]}
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                ref={(el) => {
                  inputs.current[index] = el
                }}
                className={
                  "w-11 h-9.5 text-center text14Medium text-neutral-1000 border border-neutral-450 rounded-md"
                }
              />
            ))}
          </div>
          <Button
            size={"sm"}
            color={"primary"}
            disabled={isVerifyingOtp || isRequestingOtp || otp?.join("")?.length !== OTP_LENGTH}
            onClick={handleVerifyOtp}
          >
            {isVerifyingOtp ? <Spinner size={"sm"} light /> : "Submit"}
          </Button>
        </div>
        <div className={"flex flex-row items-center justify-start gap-2"}>
          <button
            id={"resend-otp-button"}
            className={`text14Medium text-primary-600 ${showResend || sendOtpResponse?.attempts_left === 0 ? "" : "hidden"}`}
            onClick={handleSendOtp}
          >
            Resend {isRequestingOtp ? <Spinner size={"sm"} className={"ml-2 -mt-0.5"} /> : null}
          </button>
          <p
            className={`text14Regular text-neutral-900 ${sendOtpResponse?.attempts_left === 0 ? "hidden" : ""}`}
            id={"otp-resend-timer"}
          />
        </div>
        {formError ? <p className={"text14Regular text-red-600"}>{formError}</p> : null}
      </div>
    </div>
  )
}

export default CustomerOtpVerificationForm
